import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Chart from 'chart.js/auto';
import { formatCurrency } from '../CoreFunctions';

const CashFlow = () => {
  // Data
  const [ccTxns, setCcTxns] = useState([]); // revolvers (credit cards)
  const [cashTxns, setCashTxns] = useState([]); // cash account transactions
  const [categories, setCategories] = useState([]); // category metadata for rollups
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Controls
  const [rangeMode, setRangeMode] = useState('ytd'); // last30 | month | last3 | ytd | last12
  const [month, setMonth] = useState(() => {
    const d = new Date(); const mm = String(d.getMonth()+1).padStart(2,'0'); return `${d.getFullYear()}-${mm}`;
  });
  const [groupBy, setGroupBy] = useState('monthly'); // weekly | monthly | quarterly | annually
  const [cumulative, setCumulative] = useState(false);
  const [netOnly, setNetOnly] = useState(false); // if true, show Net Cash Flow instead of Inflow/Outflow
  const [viewMode, setViewMode] = useState('aggregate'); // aggregate | category

  // Chart refs
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const mousePosRef = useRef({ x: 0, y: 0 });

  // Date range
  const [startDate, endDate] = useMemo(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    let start;
    if (rangeMode === 'last30') {
      start = new Date(end); start.setDate(start.getDate() - 29); start.setHours(0,0,0,0);
    } else if (rangeMode === 'month') {
      const [y, m] = month.split('-').map(Number);
      start = new Date(y, m - 1, 1, 0,0,0,0);
      const last = new Date(y, m, 0); last.setHours(23,59,59,999);
      return [start, last];
    } else if (rangeMode === 'last3') {
      start = new Date(end); start.setMonth(start.getMonth() - 2); start.setDate(1); start.setHours(0,0,0,0);
    } else if (rangeMode === 'ytd') {
      start = new Date(end.getFullYear(), 0, 1, 0,0,0,0);
    } else if (rangeMode === 'last12') {
      start = new Date(end); start.setMonth(start.getMonth() - 11); start.setDate(1); start.setHours(0,0,0,0);
    } else {
      start = new Date(end); start.setDate(start.getDate() - 29); start.setHours(0,0,0,0);
    }
    return [start, end];
  }, [rangeMode, month]);

  // Fetch data
  useEffect(() => {
    const load = async () => {
      setLoading(true); setError('');
      try {
        const start = startDate.toISOString().slice(0,10);
        const end = endDate.toISOString().slice(0,10);
        const [rvRes, caRes, catRes] = await Promise.all([
          axios.get('/api/balance_sheet/financing/revolvers/transactions/', { params: { start_date: start, end_date: end }  }),
          axios.get('/api/balance_sheet/assets/cash_accounts/transactions/', { params: { start_date: start, end_date: end }  }),
          axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/'),
        ]);
        const rvList = Array.isArray(rvRes.data) ? rvRes.data : (rvRes.data?.transactions || []);
        setCcTxns(rvList);
        const caList = Array.isArray(caRes.data) ? caRes.data : (caRes.data?.transactions || []);
        setCashTxns(caList);
        setCategories(Array.isArray(catRes.data) ? catRes.data : []);
      } catch (e) {
        console.error('Cash Flow load error', e);
        setError('Failed to load data.');
      } finally {
        setLoading(false);
      }
    };
    if (startDate && endDate) load();
  }, [startDate, endDate]);

  // Helper: map child category to parent category
  const getParentInfo = useMemo(() => {
    const map = new Map(); // childValue -> { parentValue, parentLabel }
    categories.forEach(cat => {
      if (cat.children && cat.children.length > 0) {
        cat.children.forEach(ch => {
          map.set(String(ch.value), { value: String(cat.value), label: String(cat.label) });
        });
      }
      // Also map parent to itself
      map.set(String(cat.value), { value: String(cat.value), label: String(cat.label) });
    });
    return (val) => {
      const v = String(val || 'none');
      return map.get(v) || { value: v, label: v.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') };
    };
  }, [categories]);

  // Buckets for aggregation
  const buckets = useMemo(() => {
    if (!startDate || !endDate) return [];
    const list = [];
    const d0 = new Date(startDate);
    if (groupBy === 'weekly') {
      const day = (d0.getDay() + 6) % 7; d0.setDate(d0.getDate() - day);
    } else if (groupBy === 'monthly') {
      d0.setDate(1);
    } else if (groupBy === 'quarterly') {
      const qm = Math.floor(d0.getMonth()/3)*3; d0.setMonth(qm, 1);
    } else if (groupBy === 'annually') {
      d0.setMonth(0, 1);
    }

    const end = new Date(endDate);

    const keyFor = (date) => {
      const y = date.getFullYear();
      if (groupBy === 'weekly') {
        const tmp = new Date(date); tmp.setHours(0,0,0,0);
        const day = (tmp.getDay() + 6) % 7; const th = new Date(tmp); th.setDate(tmp.getDate() - day + 3);
        const oneJan = new Date(th.getFullYear(),0,1);
        const week = Math.floor(((th - oneJan) / 86400000 + oneJan.getDay() + 1) / 7) + 1;
        return `${th.getFullYear()}-W${String(week).padStart(2,'0')}`;
      } else if (groupBy === 'monthly') {
        return `${y}-${String(date.getMonth()+1).padStart(2,'0')}`;
      } else if (groupBy === 'quarterly') {
        const q = Math.floor(date.getMonth()/3) + 1; return `${y}-Q${q}`;
      }
      return `${y}`;
    };

    const addStep = (date) => {
      if (groupBy === 'weekly') date.setDate(date.getDate()+7);
      else if (groupBy === 'monthly') date.setMonth(date.getMonth()+1);
      else if (groupBy === 'quarterly') date.setMonth(date.getMonth()+3);
      else date.setFullYear(date.getFullYear()+1);
    };

    for (let cur = new Date(d0); cur <= end; addStep(cur)) {
      list.push({ key: keyFor(cur), start: new Date(cur) });
    }
    return list;
  }, [startDate, endDate, groupBy]);

  // Gross Inflow (positive movements)
  const inflowRows = useMemo(() => {
    const rows = [];
    const toNum = (val) => {
      try { return typeof val === 'object' && val && val.amount != null ? Number(val.amount) : Number(val); } catch(e) { return 0; }
    };

    // Identify all parent IDs across the whole set to ensure we only count leaves
    const cashParentIds = new Set();
    (cashTxns || []).forEach(t => {
      if (t.parent) cashParentIds.add(t.parent);
      if (t.children_count > 0 || (Array.isArray(t.children) && t.children.length > 0)) {
        cashParentIds.add(t.id);
      }
    });

    const ccParentIds = new Set();
    (ccTxns || []).forEach(t => {
      if (t.parent) ccParentIds.add(t.parent);
      if (t.children_count > 0 || (Array.isArray(t.children) && t.children.length > 0)) {
        ccParentIds.add(t.id);
      }
    });

    const pushCashInflow = (t) => {
      const amt = toNum(t.amount);
      if (amt > 0) {
        const cat = (t.expense_category || t.category || 'none').toLowerCase();
        const p = getParentInfo(cat);
        rows.push({ date: t.transaction_date, amount: amt, category: p });
      }
    };

    // Cash transactions: deposits/inflows are positive
    (cashTxns || []).forEach(t => {
      if (cashParentIds.has(t.id)) {
        (Array.isArray(t.children) ? t.children : []).forEach(pushCashInflow);
      } else if (!t.parent) {
        pushCashInflow(t);
      }
    });

    // CC transactions: credits/refunds/payments are negative. 
    // We only want to include these as "Inflows" if they are NOT payments/transfers to avoid double counting
    // gross flows already captured on the cash side.
    (ccTxns || []).forEach(t => {
      if (ccParentIds.has(t.id)) {
        (Array.isArray(t.children) ? t.children : []).forEach(child => {
          const amt = toNum(child.amount);
          if (amt < 0) {
            const cat = (child.expense_category || child.category || 'none').toLowerCase();
            if (cat !== 'payments' && cat !== 'transfer') {
              const p = getParentInfo(cat);
              rows.push({ date: child.transaction_date, amount: Math.abs(amt), category: p });
            }
          }
        });
        return;
      }
      if (t.parent) return;

      const amt = toNum(t.amount);
      if (amt < 0) {
        const cat = (t.expense_category || t.category || 'none').toLowerCase();
        // Exclude payments and transfers to avoid double counting with the cash-side outflow.
        // Also exclude 'none' if it's likely a payment (large amount) to reduce noise, 
        // but the user specifically complained about 32k 'None' which we now know are untagged payments.
        if (cat !== 'payments' && cat !== 'transfer') {
          // Additional heuristic: if category is 'none' and it's a large negative amount on CC, 
          // it's almost certainly a payment.
          if (cat === 'none' && Math.abs(amt) > 500) return; 

          const p = getParentInfo(cat);
          rows.push({ date: t.transaction_date, amount: Math.abs(amt), category: p });
        }
      }
    });

    return rows;
  }, [cashTxns, ccTxns, getParentInfo]);

  // Gross Outflow (negative movements)
  const outflowRows = useMemo(() => {
    const rows = [];
    const toNum = (val) => {
      try { return typeof val === 'object' && val && val.amount != null ? Number(val.amount) : Number(val); } catch(e) { return 0; }
    };

    // Identify all parent IDs across the whole set to ensure we only count leaves
    const cashParentIds = new Set();
    (cashTxns || []).forEach(t => {
      if (t.parent) cashParentIds.add(t.parent);
      if (t.children_count > 0 || (Array.isArray(t.children) && t.children.length > 0)) {
        cashParentIds.add(t.id);
      }
    });

    const ccParentIds = new Set();
    (ccTxns || []).forEach(t => {
      if (t.parent) ccParentIds.add(t.parent);
      if (t.children_count > 0 || (Array.isArray(t.children) && t.children.length > 0)) {
        ccParentIds.add(t.id);
      }
    });

    const pushCashOutflow = (t) => {
      const amt = toNum(t.amount);
      if (amt < 0) {
        const cat = (t.expense_category || t.category || 'none').toLowerCase();
        const p = getParentInfo(cat);
        rows.push({ date: t.transaction_date, amount: Math.abs(amt), category: p });
      }
    };

    // Cash transactions: payments/outflows are negative
    (cashTxns || []).forEach(t => {
      if (cashParentIds.has(t.id)) {
        (Array.isArray(t.children) ? t.children : []).forEach(pushCashOutflow);
      } else if (!t.parent) {
        pushCashOutflow(t);
      }
    });

    // CC transactions: charges are positive.
    (ccTxns || []).forEach(t => {
      if (ccParentIds.has(t.id)) {
        (Array.isArray(t.children) ? t.children : []).forEach(child => {
          const amt = toNum(child.amount);
          if (amt > 0) {
            const cat = (child.expense_category || child.category || 'none').toLowerCase();
            if (cat !== 'payments' && cat !== 'transfer') {
              const p = getParentInfo(cat);
              rows.push({ date: child.transaction_date, amount: amt, category: p });
            }
          }
        });
        return;
      }
      if (t.parent) return;

      const amt = toNum(t.amount);
      if (amt > 0) {
        const cat = (t.expense_category || t.category || 'none').toLowerCase();
        // Exclude payments and transfers to avoid double counting if they are somehow positive
        if (cat !== 'payments' && cat !== 'transfer') {
          const p = getParentInfo(cat);
          rows.push({ date: t.transaction_date, amount: amt, category: p });
        }
      }
    });

    return rows;
  }, [cashTxns, ccTxns, getParentInfo]);

  // Aggregate
  const chartData = useMemo(() => {
    const labels = buckets.map(b => b.key);

    const keyFor = (dateStr) => {
      if (!dateStr) return null; const d = new Date(dateStr); const y = d.getFullYear();
      if (groupBy === 'weekly') {
        const tmp = new Date(d); tmp.setHours(0,0,0,0);
        const day = (tmp.getDay() + 6) % 7; const th = new Date(tmp); th.setDate(tmp.getDate() - day + 3);
        const oneJan = new Date(th.getFullYear(),0,1);
        const week = Math.floor(((th - oneJan) / 86400000 + oneJan.getDay() + 1) / 7) + 1;
        return `${th.getFullYear()}-W${String(week).padStart(2,'0')}`;
      } else if (groupBy === 'monthly') {
        return `${y}-${String(d.getMonth()+1).padStart(2,'0')}`;
      } else if (groupBy === 'quarterly') {
        const q = Math.floor(d.getMonth()/3) + 1; return `${y}-Q${q}`;
      }
      return `${y}`;
    };

    if (viewMode === 'aggregate') {
      const inflowMap = new Map(labels.map(k => [k, 0]));
      const outflowMap = new Map(labels.map(k => [k, 0]));

      inflowRows.forEach(r => { const k = keyFor(r.date); if (k && inflowMap.has(k)) inflowMap.set(k, (inflowMap.get(k) || 0) + (Number(r.amount)||0)); });
      outflowRows.forEach(r => { const k = keyFor(r.date); if (k && outflowMap.has(k)) outflowMap.set(k, (outflowMap.get(k) || 0) + (Number(r.amount)||0)); });

      let inflowArr = labels.map(k => inflowMap.get(k) || 0);
      let outflowArr = labels.map(k => outflowMap.get(k) || 0).map(v => -v);

      const makeCum = (arr) => { let s = 0; return arr.map(v => { s += v; return s; }); };

      let datasets;
      if (netOnly) {
        let net = labels.map((_, i) => (inflowArr[i] + outflowArr[i]));
        if (cumulative) net = makeCum(net);
        datasets = [
          { label: 'Net Cash Flow', data: net, backgroundColor: '#60a5fa' },
        ];
      } else {
        if (cumulative) {
          inflowArr = makeCum(inflowArr);
          outflowArr = makeCum(outflowArr);
        }
        datasets = [
          { label: 'Inflow', data: inflowArr, backgroundColor: '#16a34a', stack: 'inflow' },
          { label: 'Outflow', data: outflowArr, backgroundColor: '#ef4444', stack: 'outflow' },
        ];
      }
      return { labels, datasets };
    } else {
      // Category view
      const inflowCats = new Map(); // value -> label
      const outflowCats = new Map(); // value -> label
      inflowRows.forEach(r => inflowCats.set(r.category.value, r.category.label));
      outflowRows.forEach(r => outflowCats.set(r.category.value, r.category.label));

      const inflowDatasets = Array.from(inflowCats.entries()).map(([val, label]) => {
        const data = labels.map(l => {
          let sum = 0;
          inflowRows.forEach(r => {
            if (keyFor(r.date) === l && r.category.value === val) sum += Number(r.amount);
          });
          return sum;
        });
        // Add "In: " prefix and ensure it's categorized as inflow for legend sorting
        return { 
          label: `In: ${label}`, 
          data, 
          backgroundColor: categories.find(c => c.value === val)?.color || '#16a34a', 
          stack: 'inflow',
          isFlowIn: true 
        };
      })
      .filter(ds => ds.data.some(v => v !== 0))
      .sort((a, b) => a.label.localeCompare(b.label));

      const outflowDatasets = Array.from(outflowCats.entries()).map(([val, label]) => {
        const data = labels.map(l => {
          let sum = 0;
          outflowRows.forEach(r => {
            if (keyFor(r.date) === l && r.category.value === val) sum += Number(r.amount);
          });
          return -sum;
        });
        // Add "Out: " prefix and ensure it's categorized as outflow for legend sorting
        return { 
          label: `Out: ${label}`, 
          data, 
          backgroundColor: categories.find(c => c.value === val)?.color || '#ef4444', 
          stack: 'outflow',
          isFlowIn: false
        };
      })
      .filter(ds => ds.data.some(v => v !== 0))
      .sort((a, b) => a.label.localeCompare(b.label));

      if (cumulative) {
        const makeCum = (arr) => { let s = 0; return arr.map(v => { s += v; return s; }); };
        inflowDatasets.forEach(d => d.data = makeCum(d.data));
        outflowDatasets.forEach(d => d.data = makeCum(d.data));
      }

      // Create dummy datasets as headers/separators for the legend
      const inflowHeader = {
        label: '--- INFLOWS ---',
        data: labels.map(() => 0),
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        pointStyle: 'dash',
        hidden: false
      };
      const spacerHeader = {
        label: ' ',
        data: labels.map(() => 0),
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        hidden: false
      };
      const outflowHeader = {
        label: '--- OUTFLOWS ---',
        data: labels.map(() => 0),
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        pointStyle: 'dash',
        hidden: false
      };

      return { labels, datasets: [inflowHeader, ...inflowDatasets, spacerHeader, outflowHeader, ...outflowDatasets] };
    }
  }, [buckets, inflowRows, outflowRows, groupBy, cumulative, netOnly, viewMode, categories]);

  // Chart render
  useEffect(() => {
    if (!canvasRef.current) return;
    
    // Check if we need to recreate the chart
    const labelsStr = JSON.stringify(chartData.labels);
    const datasetsMeta = chartData.datasets.map(ds => ({ label: ds.label, stack: ds.stack }));
    const datasetsMetaStr = JSON.stringify(datasetsMeta);
    
    // We use a ref to track what was last rendered to avoid flicker/loops
    if (chartRef.current && 
        chartRef.current.lastLabels === labelsStr && 
        chartRef.current.lastDatasets === datasetsMetaStr) {
      // Just update data instead of full destroy/recreate if same structure
      chartRef.current.data.labels = chartData.labels;
      chartRef.current.data.datasets = chartData.datasets;
      chartRef.current.update('none');
      return;
    }

    if (chartRef.current) { 
      chartRef.current.destroy(); 
      chartRef.current = null; 
    }
    if (!chartData.labels || chartData.labels.length === 0) return;

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: { labels: chartData.labels, datasets: chartData.datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        categoryPercentage: 0.95,
        barPercentage: 0.9,
        scales: {
          x: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
          y: { ticks: { color: '#e5e7eb' }, grid: { color: '#374151' } },
        },
        plugins: {
          legend: { 
            position: 'bottom', 
            labels: { 
              color: '#e5e7eb',
              boxWidth: 12,
              padding: 15,
              usePointStyle: true,
            } 
          },
          tooltip: {
            backgroundColor: '#1f2937', titleColor: '#e5e7eb', bodyColor: '#e5e7eb',
            titleFont: { family: 'monospace' },
            bodyFont: { family: 'monospace' },
            padding: 10,
            cornerRadius: 4,
            position: 'nearest',
            itemSort: (a, b) => Math.abs(b.parsed.y) - Math.abs(a.parsed.y),
            external: (context) => {
              const { tooltip } = context;
              if (tooltip.opacity === 0) return;

              const chart = context.chart;
              const zeroY = chart.scales.y.getPixelForValue(0);
              const isMouseOnInflowSide = mousePosRef.current.y <= zeroY;

              if (isMouseOnInflowSide) {
                tooltip.xAlign = 'left';
                tooltip.yAlign = 'bottom';
              } else {
                tooltip.xAlign = 'right';
                tooltip.yAlign = 'top';
              }
            },
            filter: (tooltipItem) => {
              const chart = tooltipItem.chart;
              const zeroY = chart.scales.y.getPixelForValue(0);
              const isMouseOnInflowSide = mousePosRef.current.y <= zeroY;
              
              const itemDataset = chart.data.datasets[tooltipItem.datasetIndex];
              if (itemDataset.label === 'Net Cash Flow') return true;
              if (itemDataset.label.trim() === '' || itemDataset.label.startsWith('--- ')) return false;

              // Filter out 0.00 values for the current period
              if (tooltipItem.parsed.y === 0) return false;

              const isItemInflow = itemDataset.label.startsWith('In:') || itemDataset.label === 'Inflow' || itemDataset.stack === 'inflow' || (itemDataset.stack === 'shared_stack' && itemDataset.isFlowIn);
              return isMouseOnInflowSide === isItemInflow;
            },
            callbacks: {
              beforeBody: (tooltipItems) => {
                if (!tooltipItems || tooltipItems.length === 0) return '';
                const label = tooltipItems[0].dataset.label;
                if (label === 'Net Cash Flow') return '';

                const isInflow = label.startsWith('In:') || label === 'Inflow' || tooltipItems[0].dataset.stack === 'inflow' || (tooltipItems[0].dataset.stack === 'shared_stack' && tooltipItems[0].dataset.isFlowIn);
                const total = tooltipItems.reduce((sum, item) => sum + Math.abs(item.parsed.y), 0);
                const title = isInflow ? 'TOTAL INFLOWS' : 'TOTAL OUTFLOWS';
                const formattedTotal = formatCurrency(total);
                // Monospace alignment: 40 chars wide
                return `${title}${formattedTotal.padStart(40 - title.length)}`;
              },
              label: (ctx) => {
                const label = ctx.dataset.label || '';
                const value = formatCurrency(Math.abs(ctx.parsed.y));
                if (label === 'Net Cash Flow') return `${label}: ${formatCurrency(ctx.parsed.y)}`;
                // Monospace alignment: 40 chars wide
                return `${label}${value.padStart(40 - label.length)}`;
              }
            }
          }
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
      }
    });
    // Store metadata to avoid redundant recreations
    chartRef.current.lastLabels = labelsStr;
    chartRef.current.lastDatasets = datasetsMetaStr;
  }, [chartData]);

  return (
    <div className="container-fluid p-4" style={{ backgroundColor: '#111827', minHeight: '100vh', color: '#f3f4f6' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0">Cash Flow Overview</h2>
        <div className="d-flex gap-2">
          <select className="form-select form-select-sm bg-dark text-light border-secondary" style={{ width: 'auto' }}
                  value={rangeMode} onChange={e => setRangeMode(e.target.value)}>
            <option value="last30">Last 30 Days</option>
            <option value="month">Specific Month</option>
            <option value="last3">Last 3 Months</option>
            <option value="ytd">Year to Date</option>
            <option value="last12">Last 12 Months</option>
          </select>
          {rangeMode === 'month' && (
            <input type="month" className="form-control form-control-sm bg-dark text-light border-secondary" style={{ width: 'auto' }}
                   value={month} onChange={e => setMonth(e.target.value)} />
          )}
          <select className="form-select form-select-sm bg-dark text-light border-secondary" style={{ width: 'auto' }}
                  value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annually">Annually</option>
          </select>
          <button className={`btn btn-sm ${cumulative ? 'btn-primary' : 'btn-outline-secondary text-light'}`}
                  onClick={() => setCumulative(!cumulative)}>Cumulative</button>
          <button className={`btn btn-sm ${viewMode === 'category' ? 'btn-primary' : 'btn-outline-secondary text-light'}`}
                  onClick={() => setViewMode(viewMode === 'category' ? 'aggregate' : 'category')}>Category View</button>
          <button className={`btn btn-sm ${netOnly ? 'btn-primary' : 'btn-outline-secondary text-light'}`}
                  onClick={() => setNetOnly(!netOnly)}>{netOnly ? 'Inflow/Outflow' : 'Net Only'}</button>
        </div>
      </div>

      {loading && <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && (
        <div className="card bg-dark border-secondary shadow-sm mb-4">
          <div className="card-body" style={{ height: '500px' }}>
            {chartData.labels.length > 0 ? (
              <canvas 
                ref={canvasRef} 
                onMouseMove={(e) => {
                  const rect = canvasRef.current.getBoundingClientRect();
                  mousePosRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
                }}
              />
            ) : (
              <div className="h-100 d-flex align-items-center justify-content-center text-muted">No data for selected range.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CashFlow;
